// Google sign-in, through Supabase's native Google provider. The project's
// auth domain (auth.mysantateresahome.com) is what Google's consent screen
// shows; the callback is <auth domain>/auth/v1/callback.
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/track";

// Started vs returned, per market (Oct 18: Felipe's first Google try on
// Tamarindo ended on Google's "400. That's an error." page and never came
// back). A start with no return within the window is a sign-in that broke
// on the provider's side.
const STARTED_KEY = "myst_oauth_started";
export const OAUTH_RETURN_WINDOW_MS = 30 * 60 * 1000;

export function markOAuthStarted(provider: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(STARTED_KEY, JSON.stringify({ provider, at: now }));
  } catch {
    /* storage blocked: the start is still counted */
  }
  track("oauth_started", { provider });
}

/** Once per sign-in: the provider and seconds since the start, then forgotten. */
export function takeOAuthReturn(now: number = Date.now()): { provider: string; seconds: number } | null {
  try {
    const raw = localStorage.getItem(STARTED_KEY);
    if (!raw) return null;
    localStorage.removeItem(STARTED_KEY);
    const { provider, at } = JSON.parse(raw) as { provider?: string; at?: number };
    if (!provider || typeof at !== "number" || now - at > OAUTH_RETURN_WINDOW_MS || now < at) return null;
    return { provider, seconds: Math.round((now - at) / 1000) };
  } catch {
    return null;
  }
}

export function reportOAuthReturn(): void {
  const ret = takeOAuthReturn();
  if (ret) track("oauth_returned", ret);
}

export async function signInWithGoogle(opts: { redirectTo: string; loginHint?: string | null }): Promise<{ error: unknown }> {
  const hint = opts.loginHint?.trim() || undefined;
  markOAuthStarted("google");
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: opts.redirectTo, ...(hint ? { queryParams: { login_hint: hint } } : {}) },
  });
  return { error };
}
